import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { LocationOn, Thermostat, Schedule, Waves, MyLocation } from '@mui/icons-material';
import { Dive } from '../types/Dive';
import type { DiveSite } from '../types/DiveSite';
import { useNavigate } from 'react-router-dom';
import { UnitPreferences, formatDepth, formatTemperature } from '../utils/unitConversions';
import { diveService } from '../services/api';
import { OptionsManager, formatDateTime } from '../utils/optionsManager';
import { geocodingService } from '../services/geocodingService';
import { DiveLocation } from '../services/userDataService';
import { getDiveDisplayName, isCoordinateFormat } from '../utils/diveDisplayName';
import { preferencesManager } from '../utils/preferencesManager';

interface DiveCardProps {
  dive: Dive;
  unitPreferences: UnitPreferences;
  accessToken?: string;
  onLocationUpdate?: (diveId: string, newLocation: string) => void;
  customLocation?: DiveLocation;
  matchedSite?: DiveSite;
}

const DiveCard: React.FC<DiveCardProps> = ({
  dive,
  unitPreferences,
  accessToken,
  onLocationUpdate,
  customLocation,
  matchedSite,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [locationDisplayPreference, setLocationDisplayPreference] = useState<'geotagged' | 'description'>('geotagged');
  const optionsManager = OptionsManager.getInstance();
  const options = optionsManager.getOptions();

  // Subscribe to preference changes
  useEffect(() => {
    // Set initial preference
    const currentPref = preferencesManager.getPreferences()?.locationDisplayPreference || 'geotagged';
    setLocationDisplayPreference(currentPref);

    // Subscribe to changes
    const unsubscribe = preferencesManager.subscribe((prefs) => {
      setLocationDisplayPreference(prefs.locationDisplayPreference || 'geotagged');
    });

    return unsubscribe;
  }, []);

  const displayName = getDiveDisplayName(dive, customLocation, locationDisplayPreference);

  const showLocateButton = isCoordinateFormat(displayName);

  const formatDuration = (duration: string) => {
    const match = duration.match(/(\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [, hours, minutes] = match;
      return `${parseInt(hours)}h ${parseInt(minutes)}m`;
    }
    return duration;
  };

  const getDiveTypeColor = (diveType: string) => {
    const isDark = theme.palette.mode === 'dark';
    
    switch (diveType) {
      case 'Mermaiding':
        return {
          background: isDark 
            ? 'linear-gradient(135deg, #2d1b33 0%, #3d2647 100%)'
            : 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          border: isDark ? '1px solid #ab47bc' : '1px solid #ba68c8',
          hoverBackground: isDark
            ? 'linear-gradient(135deg, #3d2647 0%, #4d3157 100%)'
            : 'linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%)',
          chipColor: isDark ? '#ce93d8' : '#7b1fa2'
        };
      case 'Spearfishing':
        return {
          background: isDark
            ? 'linear-gradient(135deg, #331f0d 0%, #4d2f1a 100%)'
            : 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
          border: isDark ? '1px solid #fb8c00' : '1px solid #ff9800',
          hoverBackground: isDark
            ? 'linear-gradient(135deg, #4d2f1a 0%, #663f27 100%)'
            : 'linear-gradient(135deg, #ffe0b2 0%, #ffcc80 100%)',
          chipColor: isDark ? '#ffb74d' : '#e65100'
        };
      case 'Scubadiving':
        return {
          background: isDark
            ? 'linear-gradient(135deg, #0d2b33 0%, #1a3d47 100%)'
            : 'linear-gradient(135deg, #E0F7FA 0%, #B2EBF2 100%)',
          border: isDark ? '1px solid #26c6da' : '1px solid #00ACC1',
          hoverBackground: isDark
            ? 'linear-gradient(135deg, #1a3d47 0%, #26505c 100%)'
            : 'linear-gradient(135deg, #B2EBF2 0%, #80DEEA 100%)',
          chipColor: isDark ? '#4dd0e1' : '#00838F'
        };
      case 'Freediving':
        return {
          background: isDark
            ? 'linear-gradient(135deg, #1a2e1a 0%, #263d26 100%)'
            : 'linear-gradient(135deg, #E8F5E8 0%, #C8E6C9 100%)',
          border: isDark ? '1px solid #26a69a' : '1px solid #26A69A',
          hoverBackground: isDark
            ? 'linear-gradient(135deg, #263d26 0%, #334d33 100%)'
            : 'linear-gradient(135deg, #C8E6C9 0%, #A5D6A7 100%)',
          chipColor: isDark ? '#4db6ac' : '#00695C'
        };
      case 'Snorkeling':
        return {
          background: isDark
            ? 'linear-gradient(135deg, #1f2e1a 0%, #2d3d26 100%)'
            : 'linear-gradient(135deg, #F1F8E9 0%, #DCEDC8 100%)',
          border: isDark ? '1px solid #66bb6a' : '1px solid #66BB6A',
          hoverBackground: isDark
            ? 'linear-gradient(135deg, #2d3d26 0%, #3a4d33 100%)'
            : 'linear-gradient(135deg, #DCEDC8 0%, #C8E6C9 100%)',
          chipColor: isDark ? '#81c784' : '#2E7D2F'
        };
      default:
        return {
          background: isDark
            ? 'linear-gradient(135deg, #0d2333 0%, #1a3447 100%)'
            : 'linear-gradient(135deg, #E1F5FE 0%, #B3E5FC 100%)',
          border: isDark ? '1px solid #0288d1' : '1px solid #0077BE',
          hoverBackground: isDark
            ? 'linear-gradient(135deg, #1a3447 0%, #26465c 100%)'
            : 'linear-gradient(135deg, #B3E5FC 0%, #81D4FA 100%)',
          chipColor: isDark ? '#42a5f5' : '#004D7A'
        };
    }
  };

  const handleCardClick = async () => {
    // Navigate to dive details page first
    navigate(`/dive/${dive.id}`);

    // Fetch detailed dive data in the background (including FIT file and SAC data)
    if (accessToken) {
      try {
        console.log('🏊‍♂️ Fetching detailed dive data for selected dive:', dive.id);
        const detailedData = await diveService.getDetailedDiveData(dive.id, accessToken);
        console.log('📊 Detailed dive data retrieved:', detailedData);

        // Store detailed data in sessionStorage for the dive details page
        sessionStorage.setItem(`detailedDiveData_${dive.id}`, JSON.stringify(detailedData));

        // Trigger a custom event to notify the dive details page that data is available
        window.dispatchEvent(new CustomEvent('detailedDiveDataReady', {
          detail: { diveId: dive.id, data: detailedData }
        }));
      } catch (error) {
        console.error('❌ Failed to fetch detailed dive data:', error);
        // Continue navigation even if detailed data fetch fails
      }
    }
  };

  const handleGeocodeClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card navigation

    if (isGeocodingLoading || !dive.latitude || !dive.longitude) return;

    setIsGeocodingLoading(true);
    try {
      // Use frontend geocoding service instead of backend API call
      const newLocation = await geocodingService.getLocationName(dive.latitude, dive.longitude);

      // Notify parent component about the update
      if (onLocationUpdate) {
        onLocationUpdate(dive.id, newLocation);
      }
    } catch (error) {
      console.error('Failed to geocode location:', error);
      // Could add a toast notification here
    } finally {
      setIsGeocodingLoading(false);
    }
  };

  const handleSiteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!matchedSite) {
      return;
    }

    navigate(`/dive-sites/${matchedSite.slug}`);
  };

  const colors = getDiveTypeColor(dive.diveType);

  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        background: colors.background,
        border: colors.border,
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
          background: colors.hoverBackground,
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Typography variant="h6" component="div" sx={{ flex: 1 }}>
              {showLocateButton ? 'Unknown Location' : displayName}
            </Typography>
            {showLocateButton && (
              <Button
                size="small"
                variant="outlined"
                startIcon={isGeocodingLoading ? <CircularProgress size={16} /> : <MyLocation />}
                onClick={handleGeocodeClick}
                disabled={isGeocodingLoading}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  borderColor: colors.chipColor,
                  color: colors.chipColor,
                  '&:hover': {
                    borderColor: colors.chipColor,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {isGeocodingLoading ? '' : 'Get location name'}
              </Button>
            )}
          </Box>
          <Chip
            label={dive.diveType}
            size="small"
            variant="outlined"
            sx={{
              borderColor: colors.chipColor,
              color: colors.chipColor,
              fontWeight: 'bold',
            }}
          />
        </Box>
        
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {formatDateTime(new Date(dive.startTime), options.display.timeFormat, options.display.dateFormat, dive.latitude, dive.longitude)}
        </Typography>

        {matchedSite && (
          <Box sx={{ mt: 1.5, mb: 0.5 }}>
            <Chip
              label={matchedSite.name}
              size="small"
              clickable
              onClick={handleSiteClick}
              sx={{
                maxWidth: '100%',
                fontWeight: 600,
                '& .MuiChip-label': {
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap={1} mt={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Waves fontSize="small" color="primary" />
            <Typography variant="body2">
              Max: {formatDepth(dive.maxDepth, unitPreferences.depth)} | Avg: {formatDepth(dive.avgDepth, unitPreferences.depth)}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Schedule fontSize="small" color="primary" />
            <Typography variant="body2">
              {formatDuration(dive.duration)}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Thermostat fontSize="small" color="primary" />
            <Typography variant="body2">
              {formatTemperature(dive.waterTemperature, unitPreferences.temperature)}
            </Typography>
          </Box>

          {dive.latitude && dive.longitude && (
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOn fontSize="small" color="primary" />
              <Typography variant="body2">
                {dive.latitude.toFixed(4)}, {dive.longitude.toFixed(4)}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default DiveCard;